import { NumericFormat, type NumberFormatValues, type SourceInfo } from "react-number-format";
import { TextField, type SxProps, type Theme } from "~/common/mui";
import { C_OUTLINE } from "~/common/components/icon/Colors";
import { useDebouncedState } from "~/common/hooks/useDebouncedValue";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import { ControlField } from "./ControlField";
import type { NumberInputControlViewModel } from "./controlViewModel";

export interface NumberInputControlProps extends Omit<NumberInputControlViewModel, "type" | "controlIndex"> {
  onChange: (value: string | null) => void;
  className?: string;
  sx?: SxProps<Theme>;
}

const areEquivalentNumericValues = (left: string, right: string) => {
  if (left === right) {
    return true;
  }
  if (left === "" || right === "") {
    return false;
  }

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  return Number.isFinite(leftNumber) && Number.isFinite(rightNumber) && leftNumber === rightNumber;
};

export const NumberInputControl = ({
  label,
  hideLabel,
  info,
  helperText,
  error,
  disabled = false,
  value,
  placeholder,
  debounceMs = 1000,
  readOnly = false,
  onChange,
  className,
  sx,
}: NumberInputControlProps) => {
  const [inputValue, setInputValue, flush] = useDebouncedState({
    value: value ?? "",
    onChange: (nextValue: string) => onChange(nextValue === "" ? null : nextValue),
    delay: debounceMs,
    isEqual: areEquivalentNumericValues,
  });

  const handleChange = ({ value: nextValue }: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.source === "prop") {
      return;
    }

    setInputValue(nextValue);
  };

  return (
    <ControlField
      label={label}
      hideLabel={hideLabel}
      info={info}
      helperText={helperText}
      error={Boolean(error)}
      className={className}
      sx={sx}
    >
      <NumericFormat
        customInput={TextField}
        fullWidth
        size="small"
        value={inputValue}
        valueIsNumericString
        thousandSeparator={NumberFormatter.getThousandSeparator() || false}
        decimalSeparator={NumberFormatter.getDecimalSeparator()}
        placeholder={placeholder}
        disabled={disabled}
        error={Boolean(error)}
        inputProps={{ "aria-label": label, inputMode: "decimal", readOnly }}
        sx={(theme) => ({
          "& .MuiOutlinedInput-root": { backgroundColor: theme.palette.surfaceContainer.lowest },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: C_OUTLINE },
        })}
        onValueChange={handleChange}
        onBlur={flush}
      />
    </ControlField>
  );
};
